import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

// Shown when the org has no default Xactnet address, so a blank office address isn't allowed.
const ADD_XN_REQUIRED_MESSAGE = "Enter a Xactnet address, or set an organization default first.";

type AddOfficeDialogProps = {
  open: boolean;
  existingNames: string[];
  /** When true, the org has no saved default address, so the office address can't be blank. */
  requireXn: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (name: string, xn: string) => void | Promise<void>;
};

export function AddOfficeDialog({ open, existingNames, requireXn, isSaving, onCancel, onConfirm }: AddOfficeDialogProps) {
  const [name, setName] = useState("");
  const [xn, setXn] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setXn("");
      setError(null);
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedXn = xn.trim();
  const isDuplicate = existingNames.some((n) => n.trim().toLowerCase() === trimmedName.toLowerCase());
  const needsXn = requireXn && trimmedXn.length === 0;
  // Submit stays enabled so a rule-breaking attempt surfaces the inline error and red field.
  const canSubmit = trimmedName.length > 0 && !isDuplicate && !isSaving;
  const showXnError = error === ADD_XN_REQUIRED_MESSAGE;

  const handleSubmit = async () => {
    if (trimmedName.length === 0) {
      setError("Office name is required.");
      return;
    }
    if (isDuplicate) {
      setError("An office with this name already exists.");
      return;
    }
    if (needsXn) {
      setError(ADD_XN_REQUIRED_MESSAGE);
      return;
    }
    setError(null);
    await onConfirm(trimmedName, trimmedXn);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onCancel())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add office</DialogTitle>
          <DialogDescription>Create a new office and pair it with a Xactnet address.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="add-office-name" className="text-foreground text-sm font-medium">
              Office name
            </label>
            <Input
              id="add-office-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="add-office-xn" className="text-foreground text-sm font-medium">
              Xactnet address
            </label>
            <Input
              id="add-office-xn"
              value={xn}
              onChange={(e) => {
                setXn(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              autoComplete="off"
              aria-invalid={showXnError}
              className={cn(showXnError && "border-destructive focus-visible:ring-destructive")}
            />
          </div>

          {isDuplicate && trimmedName.length > 0 ? (
            <p className="text-destructive text-sm">An office with this name already exists.</p>
          ) : error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isSaving ? "Adding…" : "Add office"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
